import { Box, Button, Container, Flex, HStack, Stack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { IWhiteLabelForm } from 'api/types';
import useCreateWhitelabelOrgMutation from 'hooks/mutations/useCreateWhiteLabelOrgMutation';
import useUpdateWhitelabelOrganizationMutation from 'hooks/mutations/useUpdateWhitelabelOrganizationMutation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { simpleInput } from '../../helpers/makeFormFields';

const WhiteLabelForm = ({ wloId, defaultValues = {} }: { wloId: number; defaultValues: any }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const whitelableUpdateMutation = useUpdateWhitelabelOrganizationMutation();
  const whitelableCreationMutation = useCreateWhitelabelOrgMutation();

  const schema = yup
    .object({
      name: yup.string().label(t('user:name')).required(t('user:form.name_required')),
      domain: yup
        .string()
        // eslint-disable-next-line no-useless-escape
        .matches(/^(https?:\/\/)[A-Za-z0-9\-\.]+$/, t('pages:whitelabel_organizations.invalid_domain'))
        .required(t('pages:whitelabel_organizations.domain_required')),
      logo_url: yup
        .string()
        .required(t('pages:whitelabel_organizations.logo_url_required'))
        .matches(/^(?:http|https):\/\/[\w.-]+\.[a-zA-Z]{2,}(?:\/\S*)?$/, t('pages:whitelabel_organizations.invalid_url')),
      auth0_org_id: yup
        .string()
        .required(t('pages:whitelabel_organizations.auth0_org_id_required'))
        .matches(/^\S*$/, t('pages:whitelabel_organizations.id_no_spaces')),
      auth0_connection_id: yup
        .string()
        .required(t('pages:whitelabel_organizations.auth0_connection_id_required'))
        .matches(/^\S*$/, t('pages:whitelabel_organizations.id_no_spaces')),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    register,
    setError,
  } = useForm<IWhiteLabelForm>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const successOrError = {
    onSuccess: () => (wloId ? navigate('/app/op/whitelabel-organizations/' + wloId) : navigate('/app/op/whitelabel-organizations')),
    onError: (error: any) => {
      const { errors } = error.response.data;

      (Object.keys(errors) as Array<keyof IWhiteLabelForm>).forEach((key) => {
        setError(key, {
          type: 'server',
          message: errors[key]!.join('. '),
        });
      });
    },
  };

  const onSubmit: SubmitHandler<IWhiteLabelForm> = (data) => {
    wloId ? whitelableUpdateMutation.mutate({ wloData: data, wloId }, successOrError) : whitelableCreationMutation.mutate(data, successOrError);
  };

  return (
    <Stack>
      <Container as="form" onSubmit={handleSubmit(onSubmit)} margin={0}>
        <Stack>
          <Box>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2}>
              {simpleInput({
                name: 'name',
                label: t('user:name'),
                register,
                errors,
                schema,
              })}
              {simpleInput({
                name: 'domain',
                label: t('pages:whitelabel_organizations.domain'),
                register,
                errors,
                schema,
              })}
            </Stack>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2}>
              {simpleInput({
                name: 'logo_url',
                label: t('pages:whitelabel_organizations.logo_url'),
                register,
                errors,
                schema,
              })}
            </Stack>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2}>
              {simpleInput({
                name: 'auth0_org_id',
                label: t('pages:whitelabel_organizations.auth0_org_id'),
                register,
                errors,
                schema,
              })}
              {simpleInput({
                name: 'auth0_connection_id',
                label: t('pages:whitelabel_organizations.auth0_connection_id'),
                register,
                errors,
                schema,
              })}
            </Stack>
          </Box>

          <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
            <HStack>
              <Button
                color={'red.600'}
                borderColor={'red.600'}
                //@ts-ignore
                _hover={'pink.600'}
                variant="outline"
                onClick={() => {
                  navigate(wloId ? `/app/op/whitelabel-organizations/${wloId}` : '/app/op/whitelabel-organizations');
                }}
              >
                {t('common:abort')}
              </Button>

              <Button type="submit" variant="primary" disabled={submitCount > 0 && !isValid}>
                {t(wloId ? 'common:update' : 'common:create')}
              </Button>
            </HStack>
          </Flex>
        </Stack>
      </Container>
    </Stack>
  );
};
export default WhiteLabelForm;
